import { CommonModule } from '@angular/common'
import { Component, ElementRef, EventEmitter, HostListener, Input, OnInit, Output } from '@angular/core'
import { FormsModule, ReactiveFormsModule, UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms'
import { MatIconModule } from '@angular/material/icon'
import { MatTooltipModule } from '@angular/material/tooltip'
import { AuthService } from '@app/modules/auth/services/auth.service'
import { LegalSetup } from '@app/modules/events/types/legal-setup.type'
import { RepositoriesService } from '@app/services/repositories.service'
import {
  NbButtonModule,
  NbCardModule,
  NbCheckboxModule,
  NbFormFieldModule,
  NbIconModule,
  NbInputModule,
  NbRadioModule,
  NbSelectModule,
  NbSpinnerModule,
  NbTooltipModule
} from '@nebular/theme'
import { PersonaFieldTypes } from '@personas/enums/persona-fields-types.enum'
import { LineSeparatorTextSize } from '@personas/interfaces/persona-field.interface'
import { PersonaFactoryService } from '@personas/models/persona.model'
import { fadeInDownOnEnterAnimation, fadeOutUpOnLeaveAnimation } from 'angular-animations'
import { LanguageService } from '../../services/language.service'
import { FormElementsValuesComponent } from '../form-elements-values/form-elements-values.component'

export type ConditionType = 'displayIf' | 'requiredIf'

/**
 * Represents a single persona field
 */
@Component({
  selector: 'ngx-personas-form-element',
  templateUrl: './form-element.component.html',
  styleUrls: ['./form-element.component.scss'],
  animations: [fadeInDownOnEnterAnimation({ delay: 0, duration: 150, translate: '10%' }), fadeOutUpOnLeaveAnimation({ delay: 0, duration: 150, translate: '10%' })],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MatIconModule,
    MatTooltipModule,
    FormElementsValuesComponent,
    NbFormFieldModule,
    NbSelectModule,
    NbInputModule,
    NbIconModule,
    NbTooltipModule,
    NbButtonModule,
    NbCardModule,
    NbSpinnerModule,
    NbCheckboxModule,
    NbRadioModule
  ]
})
export class FormElementComponent implements OnInit {
  PersonaFieldTypes = PersonaFieldTypes

  /**
   * Form element types list
   */
  public types = this.repositoriesService.FormElementTypes
  /**
   * Unique persona fields types list
   */
  private uniquePersonaFieldTypes = [PersonaFieldTypes.FULLNAME, PersonaFieldTypes.EMAIL, PersonaFieldTypes.ORGANIZATION, PersonaFieldTypes.JOB_TITLE, PersonaFieldTypes.PHONE]
  /**
   * Field types
   */
  public personaFieldTypes = PersonaFieldTypes
  /**
   * Has a name error
   */
  public nameError = null
  /**
   * Has a type error
   */
  public typeError = null
  /**
   * Persona field group
   */
  @Input()
  group: UntypedFormGroup
  /**
   * Persona field name
   */
  @Input()
  name: UntypedFormControl

  /**
   * Persona field type
   */
  @Input()
  type: UntypedFormControl
  /**
   * Persona field label
   */
  @Input()
  label: UntypedFormControl
  /**
   * Persona field is unique
   */
  @Input()
  isUnique: UntypedFormControl
  /**
   * Persona field is required
   */
  @Input()
  isRequired: UntypedFormControl
  /**
   * Persona field is disabled onsite
   */
  @Input()
  isDisabledOnsite: UntypedFormControl
  /**
   * Persona field is disabled online
   */
  @Input()
  isDisabledOnline: UntypedFormControl
  /**
   * List of available options for the persona field
   */
  @Input()
  values: UntypedFormArray

  /**
   * Current element types in the persona
   */
  @Input()
  elementTypes: number[]

  /**
   * Disables isRequired changes
   */
  @Input()
  disableRequiredChanges = false
  /**
   * Disables isRequired online changes
   */
  @Input()
  disableRequiredOnlineChanges = false

  /**
   * Disables isUnique changes
   */
  @Input()
  disableUniqueChanges = false
  /**
   * Disables field deletion
   */
  @Input()
  disableDeleteChanged = false

  @Input() eventLegalSetup: LegalSetup

  @Input() isDTCMRequiredField

  /**
   * All form elements (needed for condition modal to show available fields)
   */
  @Input() formElements: UntypedFormArray

  /**
   * Index of this element in the form elements array (for unique modal ID)
   */
  @Input() elementIndex: number = 0

  isDropdown = false

  enableConfirmation = false

  /**
   * Currently selected condition type for modal
   */
  selectedConditionType: ConditionType = 'displayIf'

  /**
   * Emits delete changes
   */
  @Output()
  deleteFieldChanged: EventEmitter<any> = new EventEmitter<any>()

  /**
   * Emits when condition modal should be opened
   */
  @Output()
  openConditionModalEvent: EventEmitter<{ element: UntypedFormGroup; conditionType: ConditionType }> = new EventEmitter()

  /**
   * Displays field option list
   */
  showMoreActions = false
  /**
   * Validators
   */
  minLabelLength = 2
  /**
   * Form
   */
  @Input() form

  private readonly NATIONALITY_LABEL = 'Nationality'
  /** Option key for line separator heading size */
  readonly LINE_SEPARATOR_TEXT_SIZE_OPTION_KEY = 'text-size'
  /** Default font size for line separator fields */
  readonly DEFAULT_LINE_SEPARATOR_TEXT_SIZE: LineSeparatorTextSize = 'medium'
  /** Available font sizes for line separator fields */
  readonly lineSeparatorTextSizes: LineSeparatorTextSize[] = ['small', 'medium', 'large']

  /**
   * Creates the component and injects it's dependencies
   * @param elementRef ElementRef
   * @param fb FormBuilder
   * @param repositoriesService RepositoriesService
   * @param pf PersonaFactory
   */
  constructor(
    private elementRef: ElementRef,
    private fb: UntypedFormBuilder,
    private repositoriesService: RepositoriesService,
    private pf: PersonaFactoryService,
    private languageService: LanguageService,
    public authService: AuthService
  ) {}

  ngOnInit(): void {
    const formElementType = this.group['value']['form_element_type_id']
    this._applyLabelValidatorsForType(formElementType)
    this._handleOptionsForType(formElementType)
  }

  /**
   * Handles clicking outside persona field option menu
   * @param event MouseEvent
   */
  @HostListener('document:click', ['$event'])
  clickOut(event) {
    if (!this.elementRef.nativeElement.contains(event.target) && this.showMoreActions) {
      this.showMoreActions = false
    }
  }
  /**
   * updates persona field property
   * @param property string
   * @param value string
   */
  update(property, value) {
    const isRequiredBoth = +this.isRequired.value === 3
    switch (property) {
      case 'type':
        if ([PersonaFieldTypes.INFORMATION_MESSAGE, PersonaFieldTypes.LINE_SEPARATOR, PersonaFieldTypes.WARNING_MESSAGE].includes(value)) {
          this.isRequired.patchValue(0)
        }
        if (
          this.eventLegalSetup !== 'NONE' &&
          ([PersonaFieldTypes.FULLNAME, PersonaFieldTypes.PHONE, PersonaFieldTypes.EMAIL].includes(value) || (value === PersonaFieldTypes.COUNTRY && this.name.value === 'country'))
        ) {
          this.isRequired.patchValue(3)
        }
        const element = { ...this.repositoriesService.FormElementTypes.find((item) => item.id === value) }
        const duplicatedElements: [] = this.form.value?.form_elements?.filter((element) => element.form_element_type_id === value)
        if (duplicatedElements?.length) element.name += ` ${duplicatedElements.length}`
        if (value === PersonaFieldTypes.STAR_RATING) {
          this.label.patchValue('How do you rate your last edition experience?')
        } else if (value === PersonaFieldTypes.LINE_SEPARATOR) {
          this.label.patchValue('')
        } else {
          this.label.patchValue(element.name)
        }
        this._applyLabelValidatorsForType(value)
        this.type.patchValue(value)
        this.patchPersonaFieldName()
        if (this.eventLegalSetup !== 'NONE' && value === PersonaFieldTypes.COUNTRY && this.name.value === 'country') this.label.patchValue(this.NATIONALITY_LABEL)
        this.loadPersonaFieldDefaultTemplate(value)
        this._toggleTimerControl(value)
        if (value === PersonaFieldTypes.LINE_SEPARATOR) {
          this._ensureLineSeparatorTextSize()
        }
        break
      case 'label':
        // For STAR_RATING fields, limit the label to 120 characters
        if (this.type.value === PersonaFieldTypes.STAR_RATING && value && value.length > 120) {
          value = value.substring(0, 120)
        }
        this.patchPersonaFieldName()
        this.label.patchValue(value)
        break
      case 'isRequiredOnline':
        const isRequiredOnsite = +this.isRequired.value === 2
        this.isRequired.patchValue((value ? (isRequiredOnsite ? 3 : 1) : isRequiredBoth ? 2 : 0) + '')
        if (this.isRequired.value == 1 || this.isRequired.value == 3) this.isDisabledOnline.patchValue(false)
        break
      case 'isRequiredOnsite':
        const isRequiredOnline = +this.isRequired.value === 1
        this.isRequired.patchValue((value ? (isRequiredOnline ? 3 : 2) : isRequiredBoth ? 1 : 0) + '')
        if (this.isRequired.value == 2 || this.isRequired.value == 3) this.isDisabledOnsite.patchValue(false)
        break
      case 'isDisabledOnline':
        if (this.isDisabledOnline) {
          this.isDisabledOnline.patchValue(value)
          this.isRequired.value == 1 ? this.isRequired.patchValue(0) : this.isRequired.value == 3 ? this.isRequired.patchValue(2) : ''
        } else this.isDisabledOnline = this.fb.control(value)
        break
      case 'isDisabledOnsite':
        if (this.isDisabledOnsite) {
          this.isDisabledOnsite.patchValue(value)
          this.isRequired.value == 2 ? this.isRequired.patchValue(0) : this.isRequired.value == 3 ? this.isRequired.patchValue(1) : ''
        } else this.isDisabledOnsite = this.fb.control(value)
        break
      case 'dropdown':
        if (!Object.prototype.hasOwnProperty.call(this.group.controls['options'].value, 'dropdown_mode')) {
          ;(this.group.controls['options'] as UntypedFormGroup).addControl('dropdown_mode', this.fb.control(value))
        } else {
          ;(this.group.controls['options'] as UntypedFormGroup).controls['dropdown_mode'].setValue(value)
        }
        this.isDropdown = value
        break
      case 'enableTimer':
        this.enableTimer = value
        break
      case 'isReadOnly':
        this.readonly = value
        break
      case 'textSize':
        this.textSize = value
        break
      case 'enableConfirmation':
        if (!Object.prototype.hasOwnProperty.call(this.group.controls['options'].value, 'enableConfirmation')) {
          ;(this.group.controls['options'] as UntypedFormGroup).addControl('enableConfirmation', this.fb.control(value))
        } else {
          ;(this.group.controls['options'] as UntypedFormGroup).controls['enableConfirmation'].setValue(value)
        }
        this.enableConfirmation = value
        break
      default:
        this[property].patchValue(value)
        break
    }
    this.languageService.addNewFormFieldEvent$.next(null)
  }

  /**
   * @description Updates persona field name if the form does not have an id (new form)
   */
  patchPersonaFieldName() {
    if (this.group.value?.id) return
    const originalName = this.repositoriesService.FormElementTypes.find((item) => item.id === this.type.value).slug
    this.name.patchValue(
      this.pf.generatePersonaFieldName(
        this.label.parent.value,
        originalName,
        (this.label.parent.parent as UntypedFormArray).controls.indexOf(this.label.parent),
        this.label.parent.parent.value,
        0
      )
    )
  }
  /**
   * Loads a default template for certain persona field types
   * @param type PersonaFieldTypes
   */
  loadPersonaFieldDefaultTemplate(type: PersonaFieldTypes) {
    this.values.controls = []
    const languages: string[] = this.form['value']['options']['languages']
    const translationObject = {}
    languages.forEach((element) => {
      translationObject[element] = ''
    })
    switch (type) {
      case this.personaFieldTypes.CHECKBOX: {
        const CHECKBOXGroup = this.fb.group({
          name: ['option1'],
          slug: ['otp1'],
          weight: [1],
          options: this.fb.group({ translations: this.fb.group(translationObject) })
        })
        this.values.push(CHECKBOXGroup)
        break
      }
      case this.personaFieldTypes.RADIO: {
        const RADIOGroup = this.fb.group({
          name: ['option1'],
          slug: ['otp1'],
          weight: [1],
          options: this.fb.group({ translations: this.fb.group(translationObject) })
        })
        this.values.push(RADIOGroup)
        break
      }
      case PersonaFieldTypes.GENDER: {
        this.values.push(
          this.fb.group({ name: this.fb.control('Male'), slug: this.fb.control('male'), weight: this.fb.control(0), options: this.fb.group({ translations: this.fb.group({}) }) })
        )
        this.values.push(
          this.fb.group({
            name: this.fb.control('Female'),
            slug: this.fb.control('female'),
            weight: this.fb.control(1),
            options: this.fb.group({ translations: this.fb.group({}) })
          })
        )

        break
      }
      case PersonaFieldTypes.AGE_GROUP: {
        this.values.push(
          this.fb.group({ name: this.fb.control('<24'), slug: this.fb.control('<24'), weight: this.fb.control(0), options: this.fb.group({ translations: this.fb.group({}) }) })
        )
        this.values.push(
          this.fb.group({
            name: this.fb.control('24-45'),
            slug: this.fb.control('24-45'),
            weight: this.fb.control(1),
            options: this.fb.group({ translations: this.fb.group({}) })
          })
        )
        this.values.push(
          this.fb.group({
            name: this.fb.control('46-65'),
            slug: this.fb.control('46-65'),
            weight: this.fb.control(2),
            options: this.fb.group({ translations: this.fb.group({}) })
          })
        )
        this.values.push(
          this.fb.group({ name: this.fb.control('>65'), slug: this.fb.control('>65'), weight: this.fb.control(3), options: this.fb.group({ translations: this.fb.group({}) }) })
        )
        break
      }
      default: {
        break
      }
    }
  }
  /**
   * Whether a type can be selected from the list
   * @param typeId PersonaFieldTypes
   * @returns boolean
   */
  canSelectType(typeId): boolean {
    return !(this.uniquePersonaFieldTypes.includes(typeId) && this.elementTypes.includes(typeId))
  }

  /**
   * Adds a new persoa field value
   */
  addFieldValue() {
    this.values.push(
      this.fb.group({
        name: [''],
        slug: [''],
        weight: [this.values.length]
      })
    )
  }
  /**
   * Whether the persona field can have values
   * @returns boolean
   */
  supportsFieldsValue(): boolean {
    return this.pf.supportsFieldsValue(this.type.value)
  }

  /**
   * Validates persona fields names
   * @param _names string[]
   */
  validateNameField(_names: string[]) {
    const nameAlreadyThere = _names.filter((element) => element.length > 0 && element === this.name.value).length > 1

    this.nameError = nameAlreadyThere ? 'This label have been used before try make it more unique' : null
    if (this.nameError && !this.name.hasError('nameAlreadyThere')) {
      this.name.setErrors({ nameIsInUse: true })
    } else if (this.nameError && this.name.hasError('nameAlreadyThere')) {
      this.name.setErrors(null)
    }
  }
  /**
   * Validates persona fields types
   * @param _types PersonaFieldTypes[]
   */
  validateElementTypeField(_types: number[]) {
    const elementType = +this.type.value
    const elementLabel = this.label.value
    const isEmailOrFullName = elementType === 5 || elementType === 3
    const fullNameIsAlreadyThere = _types.filter((_type) => _type === elementType).length > 1 && isEmailOrFullName

    this.typeError = fullNameIsAlreadyThere ? `${elementLabel} should not be used more than once in a form` : null

    if (this.typeError && !this.type.hasError('nameIsInUse')) {
      this.type.setErrors({
        nameIsInUse: true
      })
    } else if (!this.nameError && this.type.hasError('nameIsInUse')) {
      this.type.setErrors(null)
    }
  }

  /**
   * Deletes a persona field by it's index
   * @param index number
   */
  deleteFieldValue(index: number) {
    this.values.removeAt(index)
  }

  /**
   * Whether a field is required online
   */
  get requiredOnline(): boolean {
    return +this.isRequired.value === 3 || +this.isRequired.value === 1
  }
  /**
   * Whether a field is required onsite
   */
  get requiredOnsite(): boolean {
    return +this.isRequired.value === 3 || +this.isRequired.value === 2
  }
  /**
   * Whether a field is disabled online
   */
  get disabledOnline(): boolean {
    return this.isDisabledOnline == null ? false : this.isDisabledOnline.value
  }
  /**
   * Whether a field is disabled onsite
   */
  get disabledOnsite(): boolean {
    return this.isDisabledOnsite == null ? false : this.isDisabledOnsite.value
  }
  /**
   * Whether a field is read only
   */
  get readonly(): boolean {
    const readonlyControl = this.group.controls['options'].get('readonly')
    if (!readonlyControl) {
      // Initialize readonly if it doesn't exist
      ;(this.group.controls['options'] as UntypedFormGroup).addControl('readonly', this.fb.control(false))
      return false
    }
    return this.group.value.options.readonly ?? false
  }
  /**
   * Sets the readonly control for fields.
   * Automatically creates the form control if it doesn't exist in the options
   * @param {boolean} value - whether the field is read only
   */
  set readonly(value: boolean) {
    if (!this.group.controls['options'].get('readonly')) {
      ;(this.group.controls['options'] as UntypedFormGroup).addControl('readonly', this.fb.control(value))
    } else {
      ;(this.group.controls['options'] as UntypedFormGroup).controls['readonly'].setValue(value)
    }
  }
  /**
   * Whether to enable timer for date fields
   * This option is only available for DATE field types and controls whether a time picker is displayed
   */
  get enableTimer(): boolean {
    return this.group.controls['options'].get('enable_timer') ? this.group.value.options.enable_timer : false
  }
  /**
   * Sets the enable_timer control for date fields.
   * Automatically creates the form control if it doesn't exist in the options
   * and updates the label based on the timer option
   * @param {boolean} value - whether to enable the timer
   */
  set enableTimer(value: boolean) {
    if (!this.group.controls['options'].get('enable_timer')) {
      ;(this.group.controls['options'] as UntypedFormGroup).addControl('enable_timer', this.fb.control(value))
    } else {
      ;(this.group.controls['options'] as UntypedFormGroup).controls['enable_timer'].setValue(value)
    }

    // Only auto-update label if it is at its initial value ('Date' or 'Date & Time'), otherwise respect custom label
    const currentLabel = this.label.value || ''
    if (value) {
      // Enabling timer: if label is default 'Date', change to 'Date & Time'
      if (currentLabel === 'Date') {
        this.label.patchValue('Date & Time')
      }
    } else {
      // Disabling timer: if label is 'Date & Time', revert to 'Date'
      if (currentLabel === 'Date & Time') {
        this.label.patchValue('Date')
      }
    }
  }
  /**
   * Emits that a field was deleted
   */
  deleteField() {
    this.deleteFieldChanged.emit()
  }
  /**
   * Line separator font size stored in options['text-size']
   */
  get textSize(): LineSeparatorTextSize {
    const control = this.group.controls['options'].get(this.LINE_SEPARATOR_TEXT_SIZE_OPTION_KEY)
    return this._normalizeLineSeparatorTextSize(control?.value)
  }

  /**
   * Sets the line separator font size in field options
   * @param value Font size (small, medium, or large)
   */
  set textSize(value: LineSeparatorTextSize) {
    const options = this.group.controls['options'] as UntypedFormGroup
    if (!options.get(this.LINE_SEPARATOR_TEXT_SIZE_OPTION_KEY)) {
      options.addControl(this.LINE_SEPARATOR_TEXT_SIZE_OPTION_KEY, this.fb.control(value))
    } else {
      options.controls[this.LINE_SEPARATOR_TEXT_SIZE_OPTION_KEY].setValue(value)
    }
  }

  /**
   * Ensures line separator fields have a default text-size option
   */
  private _ensureLineSeparatorTextSize(): void {
    const options = this.group.controls['options'] as UntypedFormGroup
    const control = options.get(this.LINE_SEPARATOR_TEXT_SIZE_OPTION_KEY)
    if (!control) {
      this.textSize = this.DEFAULT_LINE_SEPARATOR_TEXT_SIZE
      return
    }
    const normalized = this._normalizeLineSeparatorTextSize(control.value)
    if (control.value !== normalized) {
      control.setValue(normalized)
    }
  }

  /**
   * Normalizes stored text-size values, including legacy H1/H2/H3 values
   * @param value Stored text-size option
   */
  private _normalizeLineSeparatorTextSize(value: string | null | undefined): LineSeparatorTextSize {
    const legacyMap: Record<string, LineSeparatorTextSize> = {
      H1: 'large',
      H2: 'medium',
      H3: 'small'
    }
    if (value && legacyMap[value]) {
      return legacyMap[value]
    }
    if (this.lineSeparatorTextSizes.includes(value as LineSeparatorTextSize)) {
      return value as LineSeparatorTextSize
    }
    return this.DEFAULT_LINE_SEPARATOR_TEXT_SIZE
  }

  /**
   * Conditions to enable settings
   */
  isSettingForValue() {
    return !this.isType(PersonaFieldTypes.LINE_SEPARATOR) && !this.isType(PersonaFieldTypes.INFORMATION_MESSAGE) && !this.isType(PersonaFieldTypes.WARNING_MESSAGE)
  }
  /**
   * Checks if value is of the provided type id
   * @param fieldType persona field type
   * @returns boolean
   */
  isType(fieldType: number) {
    return this.type?.value === fieldType
  }

  /**
   * Whether the label input should show a validation error state
   */
  isLabelInvalid(): boolean {
    if (this.isType(PersonaFieldTypes.LINE_SEPARATOR)) return false
    return (!this.label.valid && this.type.value) || !this.label.value
  }

  /**
   * Whether line separator font size settings should appear in the actions menu
   */
  showLineSeparatorFontSizeSettings(): boolean {
    return this.isType(PersonaFieldTypes.LINE_SEPARATOR) && !!this.label.value?.trim()
  }

  /**
   * Applies label validators based on field type
   * @param type Persona field type
   */
  private _applyLabelValidatorsForType(type: PersonaFieldTypes): void {
    if (type === PersonaFieldTypes.LINE_SEPARATOR) {
      this.label.clearValidators()
    } else {
      this.label.setValidators([Validators.minLength(this.minLabelLength), Validators.required])
    }
    this.label.updateValueAndValidity()
  }

  labelFocusOut(event) {
    if (this.isType(PersonaFieldTypes.LINE_SEPARATOR) && !event.target.value?.trim()) return
    const duplicatedLables: [] = this.form.value.form_elements.filter((element) => element.label === event.target.value)
    if (duplicatedLables.length > 1) {
      this.label.patchValue(this.label.value + ` ${duplicatedLables.length}`)
    }
  }

  /**
   * Adds a control if it doesn't exist for the given form element type
   * and updates the enableTimer or isDropdown property based on the form element type
   * @param {PersonaFieldTypes} formElementType
   */
  private _handleOptionsForType(formElementType: PersonaFieldTypes) {
    let controlName = 'dropdown_mode'
    switch (formElementType) {
      case this.personaFieldTypes.LINE_SEPARATOR:
        this._ensureLineSeparatorTextSize()
        break
      case this.personaFieldTypes.DATE:
        this.enableTimer = this.group.controls['options'].get(controlName)?.value || false
        controlName = 'enable_timer'
        break
      case this.personaFieldTypes.RADIO:
        this.isDropdown = this.group.controls['options'].get(controlName)?.value || false
        break
      case this.personaFieldTypes.WARNING_MESSAGE:
        this.enableConfirmation = this.group.controls['options'].get('enableConfirmation')?.value || false
        break
    }
  }

  /**
   * Toggles the enable_timer control for date fields
   * @param {PersonaFieldTypes} formElementType
   */
  private _toggleTimerControl(formElementType: PersonaFieldTypes) {
    // Initialize enable_timer for date fields when field type is changed to DATE
    if (formElementType == this.personaFieldTypes.DATE) {
      this._handleOptionsForType(formElementType)
      return
    }
    // Remove enable_timer for other field types when field type is changed after DATE
    if (this.group.controls['options'].get('enable_timer')) (this.group.controls['options'] as UntypedFormGroup).removeControl('enable_timer')
  }

  public get isElementDisabled(): boolean {
    return this.eventLegalSetup !== 'NONE' && this.isDTCMRequiredField
  }

  // ============================================
  // Conditional Logic (Display If / Required If)
  // ============================================

  /**
   * Whether the element has a displayIf condition configured
   */
  get hasDisplayIfCondition(): boolean {
    const conditions = this.group?.controls['options']?.get('displayIf')?.value
    return conditions !== null && conditions !== undefined
  }

  /**
   * Whether the element has a requiredIf condition configured
   */
  get hasRequiredIfCondition(): boolean {
    const conditions = this.group?.controls['options']?.get('requiredIf')?.value
    return conditions !== null && conditions !== undefined
  }

  /**
   * Gets the modal header based on condition type
   */
  get modalHeader(): string {
    return this.selectedConditionType === 'displayIf' ? 'Display If Conditions' : 'Required If Conditions'
  }

  /**
   * Gets the description text based on condition type
   */
  get descriptionText(): string {
    return this.selectedConditionType === 'displayIf'
      ? 'Show this field only when the following conditions are met:'
      : 'Make this field required when the following conditions are met:'
  }

  /**
   * Toggles a condition type for this field
   * @param conditionType 'displayIf' or 'requiredIf'
   * @param enabled Whether to enable or disable the condition
   */
  toggleCondition(conditionType: ConditionType, enabled: boolean): void {
    const options = this.group.controls['options'] as UntypedFormGroup
    if (!options.get(conditionType)) {
      options.addControl(conditionType, new UntypedFormControl(enabled ? [] : null))
    } else {
      options.get(conditionType).setValue(enabled ? [] : null)
    }
  }

  /**
   * Opens the condition configuration modal
   * @param conditionType 'displayIf' or 'requiredIf'
   */
  openConditionModal(conditionType: ConditionType): void {
    // First ensure the condition is enabled
    if (!this._hasConditionEnabled(conditionType)) {
      this.toggleCondition(conditionType, true)
    }
    this.selectedConditionType = conditionType
    // Emit event to parent to open the shared modal
    this.openConditionModalEvent.emit({
      element: this.group,
      conditionType
    })
  }

  /**
   * Checks if a condition type is enabled
   */
  private _hasConditionEnabled(conditionType: ConditionType): boolean {
    const conditions = this.group?.controls['options']?.get(conditionType)?.value
    return conditions !== null && conditions !== undefined
  }

  /**
   * @description Whether the element is read only
   * @returns boolean
   */
  public get isElementReadOnly(): boolean {
    return this.eventLegalSetup !== 'NONE' && this.group.value.form_element_type_id === PersonaFieldTypes.COUNTRY && this.group.value.label === this.NATIONALITY_LABEL
  }
}
